import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Send } from "lucide-react";
import { useI18n } from "@/lib/i18n";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const NewsletterForm = () => {
  const { t } = useI18n();
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [errors, setErrors] = useState({});

  const validate = () => {
    const next = {};
    if (!name.trim()) next.name = t("newsletter.errors.nameRequired");
    if (!email.trim()) next.email = t("newsletter.errors.emailRequired");
    else if (!EMAIL_RE.test(email.trim()))
      next.email = t("newsletter.errors.emailInvalid");
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const onSubmit = (e) => {
    e.preventDefault();
    if (!validate()) return;
    navigate("/thank_you", { state: { source: "newsletter" } });
  };

  return (
    <form
      onSubmit={onSubmit}
      data-testid="newsletter-form"
      noValidate
      className="bg-white border border-slate-200 p-6 sm:p-8 shadow-sm"
    >
      <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[#C85A32]">
        {t("newsletter.eyebrow")}
      </p>
      <h3 className="mt-2 font-heading text-2xl sm:text-3xl font-bold text-slate-900 tracking-tight">
        {t("newsletter.title")}
      </h3>
      <p className="mt-3 text-sm text-slate-600 leading-relaxed">
        {t("newsletter.subtitle")}
      </p>

      <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label
            htmlFor="newsletter-name"
            className="block text-xs font-semibold uppercase tracking-widest text-slate-600"
          >
            {t("newsletter.fields.nameLabel")}
          </label>
          <input
            id="newsletter-name"
            data-testid="newsletter-name-input"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={t("newsletter.fields.namePlaceholder")}
            className="mt-2 w-full border border-slate-300 bg-white px-3 py-2.5 text-sm text-slate-900 placeholder-slate-400 focus:border-[#0B2545] focus:outline-none focus:ring-2 focus:ring-[#0B2545]/20"
            aria-invalid={!!errors.name}
          />
          {errors.name && (
            <p
              data-testid="newsletter-name-error"
              className="mt-1.5 text-xs text-red-700"
            >
              {errors.name}
            </p>
          )}
        </div>
        <div>
          <label
            htmlFor="newsletter-email"
            className="block text-xs font-semibold uppercase tracking-widest text-slate-600"
          >
            {t("newsletter.fields.emailLabel")}
          </label>
          <input
            id="newsletter-email"
            data-testid="newsletter-email-input"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder={t("newsletter.fields.emailPlaceholder")}
            className="mt-2 w-full border border-slate-300 bg-white px-3 py-2.5 text-sm text-slate-900 placeholder-slate-400 focus:border-[#0B2545] focus:outline-none focus:ring-2 focus:ring-[#0B2545]/20"
            aria-invalid={!!errors.email}
          />
          {errors.email && (
            <p
              data-testid="newsletter-email-error"
              className="mt-1.5 text-xs text-red-700"
            >
              {errors.email}
            </p>
          )}
        </div>
      </div>

      <p className="mt-4 text-xs text-slate-500 leading-relaxed">
        {t("newsletter.consent")}
      </p>

      <div className="mt-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <p className="text-[11px] text-slate-500 max-w-md">
          {t("newsletter.disclaimer")}
        </p>
        <button
          type="submit"
          data-testid="newsletter-submit"
          className="inline-flex items-center justify-center gap-2 bg-[#0B2545] text-white px-6 py-3 text-sm font-semibold tracking-wide hover:bg-[#133763]"
        >
          <Send size={16} />
          {t("newsletter.submit")}
        </button>
      </div>
    </form>
  );
};

export default NewsletterForm;
